import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import SouthEastRoundedIcon from '@mui/icons-material/SouthEastRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getPatternTitle, managedPatternOptions } from './patternTutorialData';
import { fetchScannerOverview } from './scannerExperienceApi';
import {
  DEFAULT_SCANNER_EXCHANGE,
  DEFAULT_SCANNER_TIMEFRAME,
  DEFAULT_SCANNER_UNIVERSE,
  SCANNER_PAGE_SIZE,
  formatRelativeTargetMove,
  hasAdvancedSignalCoverage,
  resolveAverageScore,
  resolveAlignmentDescriptor,
  resolveConvictionLabel,
  resolveDirectionDescriptor,
  resolveRiskDescriptor,
  resolveScore,
  resolveVolumeDescriptor,
} from './scannerExperienceShared';

const DEFAULT_FILTERS = {
  universe: DEFAULT_SCANNER_UNIVERSE,
  exchange: DEFAULT_SCANNER_EXCHANGE,
  timeframe: DEFAULT_SCANNER_TIMEFRAME,
  assetQuery: '',
  pattern: '',
  direction: 'NON_NEUTRAL',
  convictionMin: 65,
  averageScoreMin: 65,
  volumeFilter: 'ALL',
  advancedOnly: false,
  topMoversOnly: false,
  page: 1,
};

const SCANNER_FILTERS_STORAGE_KEY = 'ecotoneai_scannerOverviewFilters';
const ANONYMOUS_SCANNER_RESULT_LIMIT = 3;
const SCANNER_FILTER_QUERY_KEYS = [
  'universe',
  'exchange',
  'timeframe',
  'asset',
  'symbol',
  'search',
  'query',
  'pattern',
  'direction',
  'scoreMin',
  'convictionMin',
  'averageScoreMin',
  'volumeFilter',
  'advancedOnly',
  'topMoversOnly',
  'page',
];

function normalizeFilters(candidate = {}) {
  return {
    universe: typeof candidate.universe === 'string' && candidate.universe ? candidate.universe : DEFAULT_FILTERS.universe,
    exchange: typeof candidate.exchange === 'string' && candidate.exchange ? candidate.exchange : DEFAULT_FILTERS.exchange,
    timeframe: typeof candidate.timeframe === 'string' && candidate.timeframe ? candidate.timeframe : DEFAULT_FILTERS.timeframe,
    assetQuery: typeof candidate.assetQuery === 'string' ? candidate.assetQuery : DEFAULT_FILTERS.assetQuery,
    pattern: typeof candidate.pattern === 'string' ? candidate.pattern : DEFAULT_FILTERS.pattern,
    direction: typeof candidate.direction === 'string' && candidate.direction ? candidate.direction : DEFAULT_FILTERS.direction,
    convictionMin: Number.isFinite(Number(candidate.convictionMin)) ? Number(candidate.convictionMin) : DEFAULT_FILTERS.convictionMin,
    averageScoreMin: Number.isFinite(Number(candidate.averageScoreMin)) ? Number(candidate.averageScoreMin) : DEFAULT_FILTERS.averageScoreMin,
    volumeFilter: typeof candidate.volumeFilter === 'string' && candidate.volumeFilter ? candidate.volumeFilter : DEFAULT_FILTERS.volumeFilter,
    advancedOnly: candidate.advancedOnly === true,
    topMoversOnly: candidate.topMoversOnly === true,
    page: Math.max(1, Number(candidate.page) || DEFAULT_FILTERS.page),
  };
}

function hasExplicitFilterParams(searchParams) {
  return SCANNER_FILTER_QUERY_KEYS.some((key) => searchParams.has(key));
}

function readStoredFilters() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_FILTERS;
  }
  try {
    const rawValue = window.localStorage.getItem(SCANNER_FILTERS_STORAGE_KEY);
    if (!rawValue) {
      return DEFAULT_FILTERS;
    }
    return normalizeFilters(JSON.parse(rawValue));
  } catch {
    return DEFAULT_FILTERS;
  }
}

function storeFilters(filters) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(SCANNER_FILTERS_STORAGE_KEY, JSON.stringify(normalizeFilters(filters)));
  } catch {
    // Ignore persistence failures and keep the scanner usable.
  }
}

function shouldRetryScannerBootstrap(filters, payload) {
  const usesBroadDirectionFetch = filters.direction !== 'BULLISH' && filters.direction !== 'BEARISH';
  return Array.isArray(payload)
    && payload.length === 0
    && usesBroadDirectionFetch
    && !filters.topMoversOnly;
}

function buildActiveFilterChips(filters) {
  const chips = [];

  if (filters.universe !== DEFAULT_FILTERS.universe) {
    chips.push({ key: 'universe', label: `Univers : ${filters.universe.toUpperCase()}` });
  }
  if (filters.timeframe !== DEFAULT_FILTERS.timeframe) {
    chips.push({ key: 'timeframe', label: `Timeframe : ${filters.timeframe}` });
  }
  if (filters.assetQuery) {
    chips.push({ key: 'assetQuery', label: `Actif : ${filters.assetQuery}` });
  }
  if (filters.direction !== DEFAULT_FILTERS.direction) {
    if (filters.direction === 'ALL') {
      chips.push({ key: 'direction', label: 'Direction : Toutes' });
    }
    if (filters.direction === 'BULLISH') {
      chips.push({ key: 'direction', label: 'Direction : Haussier' });
    }
    if (filters.direction === 'BEARISH') {
      chips.push({ key: 'direction', label: 'Direction : Baissier' });
    }
    if (filters.direction === 'NEUTRAL') {
      chips.push({ key: 'direction', label: 'Direction : Neutre' });
    }
  }
  if (filters.pattern) {
    chips.push({ key: 'pattern', label: `Pattern : ${getPatternTitle(filters.pattern)}` });
  }
  if (filters.convictionMin !== DEFAULT_FILTERS.convictionMin) {
    chips.push({ key: 'convictionMin', label: `Score >= ${filters.convictionMin}` });
  }
  if (filters.averageScoreMin !== DEFAULT_FILTERS.averageScoreMin) {
    chips.push({ key: 'averageScoreMin', label: `Score moyen >= ${filters.averageScoreMin}` });
  }
  if (filters.volumeFilter === 'CONFIRMED') {
    chips.push({ key: 'volume', label: 'Volume : confirmé' });
  }
  if (filters.volumeFilter === 'STRONG') {
    chips.push({ key: 'volume', label: 'Volume : très fort' });
  }
  if (filters.advancedOnly) {
    chips.push({ key: 'advancedOnly', label: 'Signaux avancés' });
  }
  if (filters.topMoversOnly) {
    chips.push({ key: 'topMoversOnly', label: 'Top movers' });
  }

  return chips;
}

function readFilters(searchParams) {
  const fallbackFilters = hasExplicitFilterParams(searchParams) ? DEFAULT_FILTERS : readStoredFilters();
  const scoreMinParam = searchParams.get('scoreMin');
  const convictionMinParam = searchParams.get('convictionMin');
  const averageScoreMinParam = searchParams.get('averageScoreMin');
  const assetQueryParam = searchParams.get('asset')
    || searchParams.get('symbol')
    || searchParams.get('search')
    || searchParams.get('query')
    || fallbackFilters.assetQuery;
  const advancedOnlyParam = searchParams.get('advancedOnly');
  const topMoversOnlyParam = searchParams.get('topMoversOnly');
  return {
    universe: searchParams.get('universe') || fallbackFilters.universe,
    exchange: searchParams.get('exchange') || fallbackFilters.exchange,
    timeframe: searchParams.get('timeframe') || fallbackFilters.timeframe,
    assetQuery: assetQueryParam,
    pattern: searchParams.get('pattern') || fallbackFilters.pattern,
    direction: searchParams.get('direction') || fallbackFilters.direction,
    convictionMin: Number(scoreMinParam || convictionMinParam || fallbackFilters.convictionMin),
    averageScoreMin: Number(averageScoreMinParam || fallbackFilters.averageScoreMin),
    volumeFilter: searchParams.get('volumeFilter') || fallbackFilters.volumeFilter,
    advancedOnly: advancedOnlyParam === null ? fallbackFilters.advancedOnly : advancedOnlyParam === 'true',
    topMoversOnly: topMoversOnlyParam === null ? fallbackFilters.topMoversOnly : topMoversOnlyParam === 'true',
    page: Math.max(1, Number(searchParams.get('page') || fallbackFilters.page)),
  };
}

function buildSearchParams(filters) {
  const params = new URLSearchParams();
  params.set('universe', filters.universe);
  params.set('exchange', filters.exchange);
  params.set('timeframe', filters.timeframe);
  if (filters.assetQuery) {
    params.set('asset', filters.assetQuery);
  }
  if (filters.pattern) {
    params.set('pattern', filters.pattern);
  }
  if (filters.direction !== 'ALL') {
    params.set('direction', filters.direction);
  }
  if (filters.convictionMin !== DEFAULT_FILTERS.convictionMin) {
    params.set('scoreMin', String(filters.convictionMin));
  }
  if (filters.averageScoreMin !== DEFAULT_FILTERS.averageScoreMin) {
    params.set('averageScoreMin', String(filters.averageScoreMin));
  }
  if (filters.volumeFilter !== 'ALL') {
    params.set('volumeFilter', filters.volumeFilter);
  }
  if (filters.advancedOnly) {
    params.set('advancedOnly', 'true');
  }
  if (filters.topMoversOnly) {
    params.set('topMoversOnly', 'true');
  }
  if (filters.page > 1) {
    params.set('page', String(filters.page));
  }
  return params;
}

function matchesVolumeFilter(row, volumeFilter) {
  if (volumeFilter === 'ALL') {
    return true;
  }
  const score = Number.isFinite(row?.volumeConfirmationScore) ? row.volumeConfirmationScore : 0;
  if (volumeFilter === 'CONFIRMED') {
    return Boolean(row?.volumeConfirmation || score >= 0.6);
  }
  if (volumeFilter === 'STRONG') {
    return Boolean(row?.volumeConfirmation && score >= 0.75);
  }
  return true;
}

function resolveRowDirection(row) {
  if (row?.bullish) {
    return 'BULLISH';
  }
  if (row?.bearish) {
    return 'BEARISH';
  }
  if (typeof row?.direction === 'string' && row.direction.trim()) {
    return row.direction.trim().toUpperCase();
  }
  return 'NEUTRAL';
}

function matchesDirectionFilter(row, directionFilter) {
  const direction = resolveRowDirection(row);
  if (directionFilter === 'BULLISH') {
    return direction === 'BULLISH';
  }
  if (directionFilter === 'BEARISH') {
    return direction === 'BEARISH';
  }
  if (directionFilter === 'NEUTRAL') {
    return direction === 'NEUTRAL';
  }
  if (directionFilter === 'NON_NEUTRAL') {
    return direction !== 'NEUTRAL';
  }
  return true;
}

function matchesAssetFilter(row, rawAssetQuery) {
  const assetQuery = String(rawAssetQuery || '').trim().toLowerCase();
  if (!assetQuery) {
    return true;
  }

  const symbol = String(row?.symbol || '').toLowerCase();
  const name = String(row?.name || '').toLowerCase();
  return symbol.includes(assetQuery) || name.includes(assetQuery);
}

function sortRows(rows) {
  return [...rows].sort((left, right) => {
    const scoreDelta = resolveScore(right) - resolveScore(left);
    if (scoreDelta !== 0) {
      return scoreDelta;
    }
    const averageScoreDelta = (resolveAverageScore(right) || 0) - (resolveAverageScore(left) || 0);
    if (averageScoreDelta !== 0) {
      return averageScoreDelta;
    }
    const alignmentDelta = (right?.multiTimeframeDetail?.alignmentScore || 0) - (left?.multiTimeframeDetail?.alignmentScore || 0);
    if (alignmentDelta !== 0) {
      return alignmentDelta;
    }
    return (right?.patternScore || 0) - (left?.patternScore || 0);
  });
}

function SummaryMetric({ label, value, helper, tone }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3.5,
        border: '1px solid rgba(16,37,43,0.08)',
        background: '#fffdf8',
      }}
    >
      <Typography variant="overline" sx={{ color: '#667880', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color: tone, fontWeight: 900, mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: '#5e7077' }}>
        {helper}
      </Typography>
    </Paper>
  );
}

function OpportunityCard({ row, onOpen }) {
  const direction = resolveDirectionDescriptor(row?.bullish ? 'BULLISH' : row?.bearish ? 'BEARISH' : row?.direction);
  const score = resolveScore(row);
  const averageScore = resolveAverageScore(row);
  const volume = resolveVolumeDescriptor(row);
  const alignment = resolveAlignmentDescriptor(row);
  const risk = resolveRiskDescriptor(row);
  const scoreLabel = resolveConvictionLabel(score);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.2,
        borderRadius: 4,
        border: '1px solid rgba(16,37,43,0.08)',
        background: 'linear-gradient(180deg, rgba(255,248,234,0.98) 0%, rgba(255,255,255,0.98) 100%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.4,
        minHeight: 246,
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1.4} alignItems="flex-start">
        <Box>
          <Typography variant="overline" sx={{ color: '#6b7a80', letterSpacing: '0.12em' }}>
            {row.exchange} · {row.timeframe}
          </Typography>
          <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>
            {row.symbol}
          </Typography>
          <Typography variant="body1" sx={{ color: '#43545b', fontWeight: 700 }}>
            {getPatternTitle(row.detectedPattern)}
          </Typography>
        </Box>
        <Chip
          label={direction.label}
          size="small"
          icon={direction.label === 'Baissier' ? <SouthEastRoundedIcon /> : <NorthEastRoundedIcon />}
          sx={{
            bgcolor: direction.background,
            color: direction.tone,
            fontWeight: 800,
          }}
        />
      </Stack>

      <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
        <Chip label={`Score ${score}`} size="small" sx={{ bgcolor: '#17323c', color: '#fff8eb', fontWeight: 900 }} />
        <Chip label={`Score moyen ${Number.isFinite(averageScore) ? averageScore : 'n/a'}`} size="small" sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800 }} />
        <Chip label={scoreLabel} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
        <Chip label={`Volume ${volume.label}`} size="small" sx={{ bgcolor: volume.background, color: volume.tone, fontWeight: 800 }} />
        <Chip label={`MTF ${alignment.label}`} size="small" sx={{ bgcolor: alignment.background, color: alignment.tone, fontWeight: 800 }} />
        <Chip label={`Risque ${risk.label}`} size="small" sx={{ bgcolor: risk.background, color: risk.tone, fontWeight: 800 }} />
      </Stack>

      <Typography variant="body2" sx={{ color: '#56686f', lineHeight: 1.7, flex: 1 }}>
        {row.aiSummary || 'Aucun résumé généré pour cette configuration.'}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, background: '#f5efe2' }}>
          <Typography variant="caption" sx={{ color: '#6b7a80', display: 'block' }}>Momentum</Typography>
          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{row.momentumState || 'n/a'}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, background: '#f5efe2' }}>
          <Typography variant="caption" sx={{ color: '#6b7a80', display: 'block' }}>Fakeout</Typography>
          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{row.fakeoutRisk}%</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, background: '#f5efe2' }}>
          <Typography variant="caption" sx={{ color: '#6b7a80', display: 'block' }}>Distance cible</Typography>
          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{formatRelativeTargetMove(row)}</Typography>
        </Paper>
      </Box>

      <Button
        variant="contained"
        endIcon={<ShowChartRoundedIcon />}
        onClick={onOpen}
        sx={{
          alignSelf: 'flex-start',
          borderRadius: 999,
          px: 2.2,
          py: 1,
          textTransform: 'none',
          fontWeight: 800,
          bgcolor: '#17323c',
          '&:hover': { bgcolor: '#214250' },
        }}
      >
        Voir l’analyse
      </Button>
    </Paper>
  );
}

const PatternScannerOverviewPage = ({ user = null, authResolved = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const [rows, setRows] = useState([]);
  const [analyzedAssetCount, setAnalyzedAssetCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anonymousLimitApplied, setAnonymousLimitApplied] = useState(null);
  const hasAuthenticatedUser = Boolean(user?.login);
  const isGuestScannerExperience = authResolved ? (anonymousLimitApplied ?? !hasAuthenticatedUser) : false;
  const areFiltersEditable = authResolved && !isGuestScannerExperience;

  useEffect(() => {
    let cancelled = false;

    const loadRows = async () => {
      setLoading(true);
      setError('');
      try {
        let payload = await fetchScannerOverview(filters);
        if (shouldRetryScannerBootstrap(filters, payload.rows)) {
          payload = await fetchScannerOverview(filters, { forceRefresh: true });
        }
        if (!cancelled) {
          const shouldApplyAnonymousLimit = payload.anonymousLimitApplied ?? (authResolved ? !hasAuthenticatedUser : false);
          setAnonymousLimitApplied(shouldApplyAnonymousLimit);
          setAnalyzedAssetCount(Number.isFinite(payload.analyzedAssetCount) ? payload.analyzedAssetCount : null);
          setRows(shouldApplyAnonymousLimit
            ? payload.rows.slice(0, ANONYMOUS_SCANNER_RESULT_LIMIT)
            : payload.rows);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError('Le scanner d’opportunités est indisponible pour le moment.');
          setAnalyzedAssetCount(null);
          setAnonymousLimitApplied(null);
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadRows();
    return () => {
      cancelled = true;
    };
  }, [authResolved, filters, hasAuthenticatedUser]);

  useEffect(() => {
    storeFilters(filters);
  }, [filters]);

  const setFilter = (patch) => {
    if (!areFiltersEditable) {
      return;
    }
    const nextFilters = {
      ...filters,
      ...patch,
      page: patch.page ?? (Object.prototype.hasOwnProperty.call(patch, 'page') ? patch.page : 1),
    };
    setSearchParams(buildSearchParams(nextFilters), { replace: true });
  };

  const resetFilters = () => {
    if (!areFiltersEditable) {
      return;
    }
    setSearchParams(buildSearchParams(DEFAULT_FILTERS), { replace: true });
  };

  const openDetail = (row) => {
    navigate(`/scanner/${encodeURIComponent(row.symbol)}?exchange=${encodeURIComponent(filters.exchange)}&timeframe=${encodeURIComponent(filters.timeframe)}`, {
      state: {
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const filteredRows = useMemo(() => sortRows(rows.filter((row) => {
    if (!matchesAssetFilter(row, filters.assetQuery)) {
      return false;
    }
    if (filters.pattern && row.detectedPattern !== filters.pattern) {
      return false;
    }
    if (!matchesDirectionFilter(row, filters.direction)) {
      return false;
    }
    if (resolveScore(row) < filters.convictionMin) {
      return false;
    }
    if ((resolveAverageScore(row) || 0) < filters.averageScoreMin) {
      return false;
    }
    if (!matchesVolumeFilter(row, filters.volumeFilter)) {
      return false;
    }
    if (filters.advancedOnly && !hasAdvancedSignalCoverage(row)) {
      return false;
    }
    return true;
  })), [filters.advancedOnly, filters.assetQuery, filters.averageScoreMin, filters.convictionMin, filters.direction, filters.pattern, filters.volumeFilter, rows]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / SCANNER_PAGE_SIZE));
  const currentPage = Math.min(filters.page, pageCount);
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * SCANNER_PAGE_SIZE;
    return filteredRows.slice(startIndex, startIndex + SCANNER_PAGE_SIZE);
  }, [currentPage, filteredRows]);

  const summary = useMemo(() => ({
    analyzed: analyzedAssetCount ?? rows.length,
    detected: filteredRows.length,
    strong: filteredRows.filter((row) => resolveScore(row) >= 80).length,
    institutional: filteredRows.filter((row) => (resolveAverageScore(row) || 0) >= 70).length,
  }), [analyzedAssetCount, filteredRows, rows.length]);

  const activeFilterChips = useMemo(() => buildActiveFilterChips(filters), [filters]);
  const spotlightRows = useMemo(() => filteredRows.slice(0, 3), [filteredRows]);

  return (
    <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at top, rgba(243,198,92,0.18), transparent 32%), linear-gradient(180deg, #fbf7ee 0%, #f4efe4 100%)', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Paper elevation={0} sx={{ p: { xs: 2.4, md: 3.2 }, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #264653 100%)', color: '#fff8eb' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.35fr) minmax(320px, 0.9fr)' }, gap: 2.2, alignItems: 'stretch' }}>
              <Stack spacing={1.1}>
                <Typography variant="overline" sx={{ letterSpacing: '0.12em', opacity: 0.78 }}>Détection d’opportunités</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900 }}>Pattern Scanner</Typography>
                <Typography variant="h6" sx={{ maxWidth: 780, color: 'rgba(255,248,235,0.86)', lineHeight: 1.65 }}>
                  Détection automatique des meilleures configurations chartistes pour isoler d’abord les actifs à étudier, puis seulement ensuite ouvrir l’analyse détaillée.
                </Typography>
                {isGuestScannerExperience ? (
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      mt: 0.6,
                      maxWidth: 780,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,248,235,0.12)',
                      color: '#fff8eb',
                      border: '1px solid rgba(255,248,235,0.16)',
                      '& .MuiAlert-message': { width: '100%' },
                    }}
                  >
                    Mode invité : le scanner analyse l’univers complet mais affiche jusqu’à 3 résultats et les filtres sont verrouillés. Connectez-vous pour débloquer toute la short-list et modifier le filtrage.
                  </Alert>
                ) : null}
              </Stack>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: 'rgba(255,248,235,0.1)', border: '1px solid rgba(255,248,235,0.14)' }}>
                <Stack spacing={1.2}>
                  <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'rgba(255,248,235,0.74)' }}>
                    Opportunités immédiates
                  </Typography>
                  {spotlightRows.length > 0 ? spotlightRows.map((row, index) => (
                    <Button
                      key={`${row.symbol}-${row.exchange}-${row.timeframe}-spotlight`}
                      variant="text"
                      onClick={() => openDetail(row)}
                      sx={{
                        justifyContent: 'space-between',
                        px: 1.2,
                        py: 1,
                        borderRadius: 3,
                        color: '#fff8eb',
                        bgcolor: 'rgba(255,248,235,0.08)',
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'rgba(255,248,235,0.14)' },
                      }}
                    >
                      <Stack direction="row" spacing={1.1} alignItems="center">
                        <Chip label={`#${index + 1}`} size="small" sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 900 }} />
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="subtitle2" sx={{ color: '#fff8eb', fontWeight: 900 }}>{row.symbol}</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,248,235,0.72)' }}>
                            {getPatternTitle(row.detectedPattern)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.8}>
                        <Chip label={`Score ${resolveScore(row)}`} size="small" sx={{ bgcolor: 'rgba(255,248,235,0.16)', color: '#fff8eb', fontWeight: 800 }} />
                        <Chip label={`Moyen ${Number.isFinite(resolveAverageScore(row)) ? resolveAverageScore(row) : 'n/a'}`} size="small" sx={{ bgcolor: 'rgba(243,198,92,0.18)', color: '#fff8eb', fontWeight: 800 }} />
                      </Stack>
                    </Button>
                  )) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255,248,235,0.74)', lineHeight: 1.7 }}>
                      Le radar se remplira ici dès qu’une short-list exploitable sera disponible.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.4 }, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 800 }}>Filtres</Typography>
                <Button
                  variant="text"
                  onClick={resetFilters}
                  disabled={!areFiltersEditable || activeFilterChips.length === 0}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Réinitialiser les filtres
                </Button>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }, gap: 1.2 }}>
                <TextField
                  label="Actif"
                  size="small"
                  value={filters.assetQuery}
                  disabled={!areFiltersEditable}
                  onChange={(event) => setFilter({ assetQuery: event.target.value })}
                  placeholder="BTC, ETH, SOL..."
                />
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Universe</InputLabel>
                  <Select label="Universe" value={filters.universe} onChange={(event) => setFilter({ universe: event.target.value })}>
                    <MenuItem value="top50">Top 50</MenuItem>
                    <MenuItem value="top100">Top 100</MenuItem>
                    <MenuItem value="top300">Top 300</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Exchange</InputLabel>
                  <Select label="Exchange" value={filters.exchange} onChange={(event) => setFilter({ exchange: event.target.value })}>
                    <MenuItem value="BINANCE">Binance</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Timeframe</InputLabel>
                  <Select label="Timeframe" value={filters.timeframe} onChange={(event) => setFilter({ timeframe: event.target.value })}>
                    <MenuItem value="1H">1H</MenuItem>
                    <MenuItem value="4H">4H</MenuItem>
                    <MenuItem value="1D">1D</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Direction</InputLabel>
                  <Select label="Direction" value={filters.direction} onChange={(event) => setFilter({ direction: event.target.value })}>
                    <MenuItem value="NON_NEUTRAL">Non neutre</MenuItem>
                    <MenuItem value="ALL">Toutes</MenuItem>
                    <MenuItem value="BULLISH">Haussières</MenuItem>
                    <MenuItem value="BEARISH">Baissières</MenuItem>
                    <MenuItem value="NEUTRAL">Neutre</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Pattern</InputLabel>
                  <Select label="Pattern" value={filters.pattern} onChange={(event) => setFilter({ pattern: event.target.value })}>
                    <MenuItem value="">Tous</MenuItem>
                    {managedPatternOptions.map((pattern) => (
                      <MenuItem key={pattern.value} value={pattern.value}>{pattern.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Volume</InputLabel>
                  <Select label="Volume" value={filters.volumeFilter} onChange={(event) => setFilter({ volumeFilter: event.target.value })}>
                    <MenuItem value="ALL">Tous</MenuItem>
                    <MenuItem value="CONFIRMED">Confirmé</MenuItem>
                    <MenuItem value="STRONG">Très fort</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Score min</InputLabel>
                  <Select label="Score min" value={filters.convictionMin} onChange={(event) => setFilter({ convictionMin: Number(event.target.value) })}>
                    <MenuItem value={40}>40</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={60}>60</MenuItem>
                    <MenuItem value={65}>65</MenuItem>
                    <MenuItem value={70}>70</MenuItem>
                    <MenuItem value={80}>80</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Score moyen min</InputLabel>
                  <Select label="Score moyen min" value={filters.averageScoreMin} onChange={(event) => setFilter({ averageScoreMin: Number(event.target.value) })}>
                    <MenuItem value={40}>40</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={60}>60</MenuItem>
                    <MenuItem value={65}>65</MenuItem>
                    <MenuItem value={70}>70</MenuItem>
                    <MenuItem value={80}>80</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}>
                <FormControlLabel control={<Switch checked={filters.advancedOnly} disabled={!areFiltersEditable} onChange={(event) => setFilter({ advancedOnly: event.target.checked })} />} label="Signaux avancés uniquement" />
                <FormControlLabel control={<Switch checked={filters.topMoversOnly} disabled={!areFiltersEditable} onChange={(event) => setFilter({ topMoversOnly: event.target.checked })} />} label="Top movers uniquement" />
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.8 }}>
                  Filtres actifs
                </Typography>
                {activeFilterChips.length > 0 ? (
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    {activeFilterChips.map((chip) => (
                      <Chip key={chip.key} label={chip.label} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#5c6d74' }}>
                    Aucun filtre additionnel actif. La short-list reflète le cadrage standard du scanner.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>

          {loading ? <LinearProgress sx={{ height: 8, borderRadius: 999 }} /> : null}
          {error ? <Alert severity="warning">{error}</Alert> : null}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
            <SummaryMetric label="Actifs analysés" value={summary.analyzed} helper="Couverture exploitable sur la fenêtre courante." tone="#17323c" />
            <SummaryMetric label="Setups détectés" value={summary.detected} helper="Short-list après tri score décroissant." tone="#214f77" />
            <SummaryMetric label="Scores forts" value={summary.strong} helper="Score au moins égal à 80." tone="#1f7a4d" />
            <SummaryMetric label="MTF solides" value={summary.institutional} helper="Score moyen 1H/4H/1D au moins égal à 70." tone="#8d6c16" />
          </Box>

          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.4 }, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>Top opportunités</Typography>
                <Typography variant="body2" sx={{ color: '#5c6d74' }}>
                  Les cartes sont triées par score décroissant pour mettre d’abord les setups les plus actionnables.
                </Typography>
              </Box>
              <Chip icon={<InsightsRoundedIcon />} label={`Page ${currentPage}/${pageCount} · Top ${SCANNER_PAGE_SIZE}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
            </Stack>

            {(!loading && paginatedRows.length === 0) ? (
              <Alert severity="info">Aucune opportunité ne correspond aux filtres actifs.</Alert>
            ) : null}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
              {paginatedRows.map((row) => (
                <OpportunityCard
                  key={`${row.symbol}-${row.exchange}-${row.timeframe}`}
                  row={row}
                  onOpen={() => openDetail(row)}
                />
              ))}
            </Box>

            {pageCount > 1 ? (
              <Stack direction="row" justifyContent="center" sx={{ mt: 2.4 }}>
                <Pagination page={currentPage} count={pageCount} color="primary" onChange={(_, page) => setFilter({ page })} />
              </Stack>
            ) : null}
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
};

export default PatternScannerOverviewPage;
